import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, Layers } from 'lucide-react';
import { CAPABILITIES_MODULE_ID, CAPABILITY_KEYS } from '../../../api/Navigation/moduleConfig';
import {
  getCompanyConfigForEdit,
  getCompanyDemoDrift,
  removeStoredConfigRow,
  revokeDemoSurface,
  type CompanyConfigForEdit,
  type DemoDriftCompany,
  type DemoDriftGrant,
  type StoredRowRef,
} from '../../../api/Navigation/moduleConfigAdmin';
import { mobileModules, navigationModules, pageTabGroups } from '../../../components/layout/navigationModules';
import type { SurfaceNamer } from './FleetTable';
import SafeDefaultsReview from './SafeDefaultsReview';
import { pageKey } from './useModuleConfigEditor';

/**
 * Stored rows of the open tenant that the switch table cannot show (CO-862), and the way to remove
 * one on purpose. Nothing here is removed implicitly, and none of it is what the tenant's users see
 * through a demo grant: this is the stored layer underneath.
 *
 *  - Stored on, not granted: a demo key with an ON row and no grant. Shows nothing; not the safe default.
 *  - Not on this screen: a row whose key the navigation registry does not hold, among them rows kept
 *    under an older module id (CO-496). The runtime still reads a page row by its path.
 *  - No switch here: a module row stored for a sub-module. The table draws sub-modules as headings,
 *    while the runtime honours the row.
 *  - Grants for a key that has left the demo list.
 *
 * The server refuses to remove a stored OFF row, because a missing row reads as ON: removing must
 * never switch anything on. Such a row is listed with that said and no Remove.
 *
 * The panel reads the tenant's rows itself (drift, and the config for its version), because the
 * editor does not hand out its version. After a remove or a tidy-up the stored version has changed,
 * so the page re-reads the editor's baseline through `onStoredRowsChanged`.
 */

/** HR-owned row (`RolePageOverlay.ChiefSelfServiceModuleId`): not the Operator's to list or remove. */
const CHIEF_SELF_SERVICE_MODULE_ID = 'role-chief';
const lower = (value: string) => value.toLowerCase();

/** Every key the switch table can draw, lower-cased; the registry is static, so this is built once. */
const registry = (() => {
  const pageKeys = new Set<string>();
  const paths = new Set<string>();
  const moduleIds = new Set<string>();
  const subModuleIds = new Set<string>();
  navigationModules.forEach(module => {
    moduleIds.add(lower(module.id));
    module.pages.forEach(page => {
      pageKeys.add(lower(pageKey(module.id, page.path)));
      paths.add(lower(page.path));
    });
    (module.subModules ?? []).forEach(sub => {
      subModuleIds.add(lower(sub.id));
      sub.pages.forEach(page => {
        pageKeys.add(lower(pageKey(sub.id, page.path)));
        paths.add(lower(page.path));
      });
    });
  });
  pageTabGroups.forEach(group => group.tabs.forEach(tab => pageKeys.add(lower(pageKey(group.id, tab.id)))));
  mobileModules.forEach(module => {
    moduleIds.add(lower(module.id));
    module.pages.forEach(page => pageKeys.add(lower(pageKey(module.id, page.id))));
  });
  CAPABILITY_KEYS.forEach(key => pageKeys.add(lower(pageKey(CAPABILITIES_MODULE_ID, key))));
  return { pageKeys, paths, moduleIds, subModuleIds };
})();

interface DriftItem {
  id: string;
  row: StoredRowRef;
  label: string;
  /** The stored key as it is written, shown in a fixed-width face. */
  code: string;
  on: boolean;
  note?: string;
  /** False for an OFF row the server would refuse to remove. */
  removable: boolean;
}

interface DriftPanelProps {
  companyId: number;
  companyName: string;
  namer: SurfaceNamer;
  /** Changes when the tenant's grants change (stale ON rows depend on them) and after each accepted
   *  save of the editor (the stored rows and their version are different then). */
  refreshKey: string;
  /** True while the editor saves or reloads. */
  disabled: boolean;
  onStoredRowsChanged: () => void;
  onGrantsChanged: () => void;
}

type Load =
  | { status: 'loading' }
  | { status: 'failed'; refused: boolean }
  | { status: 'ready'; drift: DemoDriftCompany; config: CompanyConfigForEdit };

const buttonBase =
  'inline-flex min-h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-lg border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50';
const quietButtonClass = `${buttonBase} border-border bg-card text-foreground hover:bg-muted`;
const removeButtonClass = `${buttonBase} border-destructive/40 bg-card text-destructive hover:bg-destructive/10`;
const destructiveButtonClass = `${buttonBase} border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90`;

const rowId = (row: StoredRowRef) =>
  lower(row.kind === 'page' ? `page|${row.moduleId}|${row.pagePath}` : `module|${row.moduleId}`);
const rowCode = (row: StoredRowRef) => (row.kind === 'page' ? `${row.moduleId} : ${row.pagePath}` : row.moduleId);

export default function DriftPanel({
  companyId,
  companyName,
  namer,
  refreshKey,
  disabled,
  onStoredRowsChanged,
  onGrantsChanged,
}: DriftPanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  /** A read is in flight. The rows on screen may be behind the stored ones until it answers, and a
   *  Remove sent with their version would be refused as changed elsewhere, so no action starts. */
  const [reading, setReading] = useState(true);
  /** The row whose Remove was pressed and waits for the second press. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tidying, setTidying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReading(true);
    (async () => {
      const [drift, config] = await Promise.all([getCompanyDemoDrift(companyId), getCompanyConfigForEdit(companyId)]);
      // A cancelled read was replaced by a newer one, which owns `reading` from here.
      if (cancelled) return;
      setReading(false);
      if (drift.ok && config.ok) {
        setLoad({ status: 'ready', drift: drift.value, config: config.config });
        return;
      }
      const status = !drift.ok ? drift.status : !config.ok ? config.status : null;
      setLoad({ status: 'failed', refused: status === 401 || status === 403 });
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey, reloadToken]);

  if (load.status === 'loading') return null;
  if (load.status === 'failed') {
    return (
      <div
        role="alert"
        data-testid="module-config-drift-load-error"
        className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      >
        <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1">
          {load.refused ? t("You don't have permission to perform this action.") : t('moduleConfig.drift.loadFailed')}
        </span>
        {!load.refused && (
          <button type="button" className={quietButtonClass} onClick={() => setReloadToken(n => n + 1)}>
            {t('Retry')}
          </button>
        )}
      </div>
    );
  }

  const { drift, config } = load;

  /* ── The lists. A row is named once: a stale demo row is not repeated among the unknown ones. ── */

  const staleOn: DriftItem[] = drift.staleOn.map(stale => {
    const row: StoredRowRef =
      stale.surfaceKind === 'module'
        ? { kind: 'module', moduleId: stale.rowModuleId }
        : { kind: 'page', moduleId: stale.rowModuleId, pagePath: stale.surfaceKey };
    return { id: rowId(row), row, label: namer(stale).label, code: rowCode(row), on: stale.isEnabled, removable: stale.isEnabled };
  });
  const named = new Set(staleOn.map(item => item.id));

  const offPaths = new Map<string, number>();
  config.pages.forEach(page => {
    if (!page.isEnabled) offPaths.set(lower(page.pagePath), (offPaths.get(lower(page.pagePath)) ?? 0) + 1);
  });
  const unknown: DriftItem[] = [
    ...config.pages
      .filter(page => lower(page.moduleId) !== CHIEF_SELF_SERVICE_MODULE_ID && !registry.pageKeys.has(lower(pageKey(page.moduleId, page.pagePath))))
      .map((page): DriftItem => {
        const row: StoredRowRef = { kind: 'page', moduleId: page.moduleId, pagePath: page.pagePath };
        const knownPath = registry.paths.has(lower(page.pagePath));
        return {
          id: rowId(row),
          row,
          label: knownPath ? namer({ surfaceKind: 'page', surfaceKey: page.pagePath }).label : page.pagePath,
          code: rowCode(row),
          on: page.isEnabled,
          note: knownPath ? t('moduleConfig.drift.olderModule') : undefined,
          // An OFF row may go only while another OFF row keeps the same path off.
          removable: page.isEnabled || (offPaths.get(lower(page.pagePath)) ?? 0) > 1,
        };
      }),
    ...config.modules
      .filter(module => {
        const id = lower(module.moduleId);
        return id !== CHIEF_SELF_SERVICE_MODULE_ID && !registry.moduleIds.has(id) && !registry.subModuleIds.has(id);
      })
      .map((module): DriftItem => {
        const row: StoredRowRef = { kind: 'module', moduleId: module.moduleId };
        return { id: rowId(row), row, label: module.moduleId, code: rowCode(row), on: module.isEnabled, removable: module.isEnabled };
      }),
  ].filter(item => !named.has(item.id));

  const unrendered: DriftItem[] = config.modules
    .filter(module => registry.subModuleIds.has(lower(module.moduleId)))
    .map((module): DriftItem => {
      const row: StoredRowRef = { kind: 'module', moduleId: module.moduleId };
      return {
        id: rowId(row),
        row,
        label: namer({ surfaceKind: 'module', surfaceKey: module.moduleId }).label,
        code: rowCode(row),
        on: module.isEnabled,
        note: module.isEnabled ? undefined : t('moduleConfig.drift.subModuleOff'),
        removable: module.isEnabled,
      };
    })
    .filter(item => !named.has(item.id));

  const count = staleOn.length + unknown.length + unrendered.length + drift.orphanGrants.length;
  const locked = disabled || reading || busyId !== null || tidying;
  const canTidy = drift.staleOn.length > 0 || drift.missingRowCount > 0;

  const remove = async (item: DriftItem) => {
    if (locked) return;
    setBusyId(item.id);
    setNotice(null);
    const result = await removeStoredConfigRow(companyId, item.row, config.version);
    setBusyId(null);
    setConfirming(null);
    if (result.ok) {
      setLoad({ status: 'ready', drift, config: result.config });
      setReloadToken(n => n + 1);
      onStoredRowsChanged();
      return;
    }
    if (result.outcome === 'conflict') {
      // Someone saved in between. Nothing was removed; show the rows as they are now.
      setNotice(t('moduleConfig.drift.conflict'));
      setReloadToken(n => n + 1);
      onStoredRowsChanged();
      return;
    }
    setNotice(result.reason ? t('moduleConfig.drift.removeFailedWithReason', { reason: result.reason }) : t('moduleConfig.drift.removeFailed'));
    if (result.outcome === 'unknown') setReloadToken(n => n + 1);
  };

  const revoke = async (grant: DemoDriftGrant) => {
    if (locked) return;
    const id = `grant|${grant.surfaceKind}|${grant.moduleId}|${grant.surfaceKey}`;
    setBusyId(id);
    setNotice(null);
    const result = await revokeDemoSurface(companyId, grant);
    setBusyId(null);
    if (!result.ok) {
      setNotice(result.reason ? t('moduleConfig.demo.actionFailedWithReason', { reason: result.reason }) : t('moduleConfig.demo.actionFailed'));
      return;
    }
    setReloadToken(n => n + 1);
    onGrantsChanged();
  };

  const group = (key: 'staleOn' | 'unknownRows' | 'unrendered', items: DriftItem[]) =>
    items.length > 0 && (
      <div data-testid={`module-config-drift-${key}`} className="px-4 py-2.5">
        <h3 className="text-xs font-medium text-foreground">{t(`moduleConfig.drift.${key}`, { total: items.length })}</h3>
        <p className="max-w-[75ch] text-xs leading-relaxed text-muted-foreground">{t(`moduleConfig.drift.${key}.hint`)}</p>
        <ul className="mt-1.5 space-y-1.5">
          {items.map(item => (
            <li key={item.id} data-testid={`module-config-drift-row-${item.id}`} data-on={item.on ? 'true' : 'false'} className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {item.label !== item.code && <span className="min-w-0 truncate text-sm text-foreground">{item.label}</span>}
              {/* A storage key, so it reads left to right in every language. */}
              <span dir="ltr" className="min-w-0 truncate font-mono text-xs text-muted-foreground">{item.code}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {t(item.on ? 'moduleConfig.state.onSet' : 'moduleConfig.state.offSet')}
              </span>
              {item.note && <span className="text-xs text-muted-foreground">{item.note}</span>}
              <span className="ms-auto flex flex-wrap items-center gap-2">
                {!item.removable && <span className="text-xs text-muted-foreground">{t('moduleConfig.drift.offStays')}</span>}
                {item.removable && confirming !== item.id && (
                  <button
                    type="button"
                    className={removeButtonClass}
                    disabled={locked}
                    aria-label={t('moduleConfig.drift.removeLabel', { key: item.code, company: companyName })}
                    data-testid={`module-config-drift-remove-${item.id}`}
                    onClick={() => setConfirming(item.id)}
                  >
                    {t('moduleConfig.drift.remove')}
                  </button>
                )}
                {item.removable && confirming === item.id && (
                  <>
                    <span className="text-xs text-foreground">{t('moduleConfig.drift.removeConfirm')}</span>
                    <button
                      type="button"
                      className={destructiveButtonClass}
                      disabled={locked}
                      aria-busy={busyId === item.id}
                      data-testid={`module-config-drift-remove-confirm-${item.id}`}
                      onClick={() => remove(item)}
                    >
                      {t('moduleConfig.drift.removeSubmit')}
                    </button>
                    <button type="button" className={quietButtonClass} disabled={locked} onClick={() => setConfirming(null)}>
                      {t('Cancel')}
                    </button>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <section
      aria-labelledby={headingId}
      data-testid="module-config-drift"
      data-drift-count={count}
      className="mb-6 overflow-hidden rounded-lg border border-border bg-card"
    >
      <h2 id={headingId}>
        <button
          type="button"
          data-testid="module-config-drift-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(previous => !previous)}
          className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 text-start transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none ${
              open ? '' : '-rotate-90 rtl:rotate-90'
            }`}
          />
          <Layers aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">{t('moduleConfig.drift.title')}</span>
          <span data-testid="module-config-drift-count" className="text-xs text-muted-foreground">
            {count > 0 ? t('moduleConfig.drift.count', { count }) : t('moduleConfig.drift.none')}
          </span>
        </button>
      </h2>

      <div id={panelId} hidden={!open} className="divide-y divide-border border-t border-border">
        {open && (
          <>
            <p className="max-w-[75ch] bg-muted px-4 py-2.5 text-xs leading-relaxed text-muted-foreground">
              {t('moduleConfig.drift.intro')}
            </p>
            {notice && (
              <p role="alert" data-testid="module-config-drift-notice" className="flex items-start gap-2 px-4 py-2.5 text-sm text-destructive">
                <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                {notice}
              </p>
            )}
            {group('staleOn', staleOn)}
            {group('unknownRows', unknown)}
            {group('unrendered', unrendered)}
            {drift.orphanGrants.length > 0 && (
              <div data-testid="module-config-drift-orphanGrants" className="px-4 py-2.5">
                <h3 className="text-xs font-medium text-foreground">
                  {t('moduleConfig.drift.orphanGrants', { total: drift.orphanGrants.length })}
                </h3>
                <p className="max-w-[75ch] text-xs leading-relaxed text-muted-foreground">{t('moduleConfig.drift.orphanGrants.hint')}</p>
                <ul className="mt-1.5 space-y-1.5">
                  {drift.orphanGrants.map(grant => (
                    <li key={`${grant.surfaceKind}|${grant.moduleId}|${grant.surfaceKey}`} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span dir="ltr" className="min-w-0 truncate font-mono text-xs text-muted-foreground">{grant.surfaceKey}</span>
                      <span className="text-xs text-muted-foreground">{t(`moduleConfig.demo.kind.${grant.surfaceKind}`)}</span>
                      <button
                        type="button"
                        className={`${removeButtonClass} ms-auto`}
                        disabled={locked}
                        aria-label={t('moduleConfig.demo.revokeLabel', { name: grant.surfaceKey, company: companyName })}
                        onClick={() => revoke(grant)}
                      >
                        {t('moduleConfig.demo.revoke')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(canTidy || count === 0) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
                <p data-testid="module-config-drift-missing" className="min-w-0 flex-1 text-xs leading-relaxed text-muted-foreground">
                  {drift.missingRowCount > 0
                    ? t('moduleConfig.drift.noRow', { count: drift.missingRowCount })
                    : count === 0
                      ? t('moduleConfig.drift.clean')
                      : t('moduleConfig.drift.tidyHint')}
                </p>
                {canTidy && !tidying && (
                  <button type="button" className={quietButtonClass} disabled={locked} data-testid="module-config-drift-tidy" onClick={() => setTidying(true)}>
                    {t('moduleConfig.drift.tidy')}
                  </button>
                )}
              </div>
            )}
            {tidying && (
              <SafeDefaultsReview
                embedded
                companyIds={[companyId]}
                namer={namer}
                onApplied={() => {
                  setReloadToken(n => n + 1);
                  onStoredRowsChanged();
                }}
                onClose={() => setTidying(false)}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}
